import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { EmptyState } from '../../group_leader/components/common'
import { makeT } from '../../group_leader/helpers/i18n'
import { buildColumns } from '../helpers/columns'
import {
  extractLeaderboardEntries,
  extractMatches,
  extractMultiMatchEntries,
  gameDisplayName,
  isMatchPlay,
  isMultiMatchPlay,
  withoutEmptyRows,
} from '../helpers/results'
import MatchPlayLeaderboard from './leaderboard/MatchPlayLeaderboard'
import StandardLeaderboard from './leaderboard/StandardLeaderboard'
import ResultsCard from './ResultsCard'

const t = makeT('group_leader.event_results.leaderboard')

// One game's results: a `score` column called "Match" or "Matches…" is match play.
const TournamentLeaderboard = ({ tournament, isLive, hasScores = true }) => {
  const name = gameDisplayName(tournament.name || tournament.tournament_name) || t('default_game_name')
  const leaderboard = tournament.leaderboard
  const matchPlay = isMatchPlay(tournament)
  const isMulti = matchPlay && isMultiMatchPlay(leaderboard)
  const hidesEmptyRows = useSelector((state) => (state.misc.data.emptyRowGameIds || []).includes(String(tournament.id)))

  const allEntries = matchPlay ? [] : extractLeaderboardEntries(tournament)
  const entries = hidesEmptyRows ? withoutEmptyRows(allEntries) : allEntries
  const columns = matchPlay ? [] : buildColumns(leaderboard, entries)
  const rowCount = matchPlay
    ? (isMulti ? extractMultiMatchEntries(leaderboard).length : extractMatches(leaderboard).length)
    : entries.length

  // Lists the field before any score, as the leaderboard page does; skins (no score/thru) waits.
  const visibility = leaderboard?.event?.column_visibility
  const showsFieldPreScore = Boolean(visibility && (visibility.thru || visibility.score))
  const showResults = rowCount > 0 && (hasScores || showsFieldPreScore)

  // Not computed or not scored yet says so, rather than claiming nobody has scored.
  const emptyMessage = tournament.available === false || !hasScores ? t('computing') : t('no_results')

  const countLabel = matchPlay
    ? t('match_count', { count: rowCount })
    : t('entry_count', { count: rowCount })

  return (
    <ResultsCard title={name} subtitle={showResults ? countLabel : null} isLive={isLive}>
      {!showResults && <EmptyState title={emptyMessage}/>}
      {showResults && (matchPlay
        ? <MatchPlayLeaderboard leaderboard={leaderboard} name={name} isMulti={isMulti}/>
        : <StandardLeaderboard entries={entries} columns={columns} name={name}/>
      )}
    </ResultsCard>
  )
}

TournamentLeaderboard.propTypes = {
  tournament: PropTypes.object.isRequired,
  isLive: PropTypes.bool,
  // False until the round has a score in; skins ships a roster blob up front.
  hasScores: PropTypes.bool,
}

export default TournamentLeaderboard
